import {
  Button,
  Stack,
  Title,
  Space,
  TextInput,
  ScrollArea,
  Text,
} from '@mantine/core';
import { LoginWrapper } from '../LoginWrapper';
import { BirthDateModalPicker } from './Components/BirthDateModalPicker';
import { useNavigate } from 'react-router-dom';
import { PageRoutes } from '../../constants/routes';
import { MobileLogo } from './SignUpEmail';
import { primaryColor } from '../../constants/theme';
import { useOnboarding } from './SignUpContext';
import { ProgressBar } from './Components/ProgressBar';
import { InformationIconWithText } from './Components/InformationIconWithMessage';
import { useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../auth/AuthContext';

export const ConfirmDetails = () => {
  const radius = 'lg';
  const navigate = useNavigate();
  const {
    form,
    isLoading,
    onSubmit,
    isError,
    isProfessional,
    isSuccess,
    resetForm,
  } = useOnboarding();
  const { getProfileAndRedirect } = useApp();
  const { accessToken } = useAuth();

  const errorStyle = {
    error: {
      textAlign: 'left' as const,
      size: 'md' as const,
    },
    description: {
      textAlign: 'left' as const,
    },
  };

  // clean up state on mount
  useEffect(() => {
    resetForm();
  }, []);

  const onNext = () => {
    form.validate();
    if (form.isValid()) {
      if (form.values.isProfessional) {
        navigate(PageRoutes.ONBOARDING_CATEGORIES);
      } else {
        onSubmit();
      }
    }
  };

  useEffect(() => {
    if (isSuccess) {
      getProfileAndRedirect(accessToken as string);
    }
  }, [isSuccess]);

  return (
    <LoginWrapper animate={false}>
      <MobileLogo />
      <Stack>
        <ScrollArea h="90%">
          <Stack align="left">
            <Title order={2} c="black">
              Confirm Your Details
            </Title>
            {isProfessional && (
              <Title
                order={4}
                mt="sm"
                data-testid={ConfirmDetailsTestIds.PERSONAL_PRIVATE_INFO_TITLE}
              >
                Personal Private Information
              </Title>
            )}
            <TextInput
              styles={errorStyle}
              radius={radius}
              {...form.getInputProps('firstName')}
              size="md"
              placeholder="First Name"
              label={'First Name'}
              data-testid={ConfirmDetailsTestIds.TEXT_INPUT_FIRST_NAME}
            ></TextInput>
            <TextInput
              styles={errorStyle}
              radius={radius}
              size="md"
              placeholder="Last Name"
              label={'Last Name'}
              {...form.getInputProps('lastName')}
              data-testid={ConfirmDetailsTestIds.TEXT_INPUT_LAST_NAME}
            ></TextInput>
            <BirthDateModalPicker
              radius={radius}
              styles={errorStyle}
              size="md"
              placeholder="Select your date of birth"
              label="Date of Birth"
              {...form.getInputProps('dateOfBirth')}
              rightSection={
                <InformationIconWithText
                  message={InfomrationMessages.DATE_OF_BIRTH}
                  testId={ConfirmDetailsTestIds.DATE_OF_BIRTH_INFO}
                />
              }
            />
            {isProfessional && (
              <>
                <Title
                  order={4}
                  mt="sm"
                  data-testid={ConfirmDetailsTestIds.PUBLIC_INFO_TITLE}
                >
                  Public Information
                </Title>
                <TextInput
                  styles={errorStyle}
                  radius={radius}
                  {...form.getInputProps('displayName')}
                  size="md"
                  placeholder="The name you go by publically"
                  label={'Display Name'}
                  data-testid={ConfirmDetailsTestIds.TEXT_INPUT_DISPLAY_NAME}
                ></TextInput>
                <TextInput
                  styles={errorStyle}
                  radius={radius}
                  size="md"
                  placeholder="e.g. Content Creator"
                  label={'Headline'}
                  {...form.getInputProps('headline')}
                  rightSection={
                    <InformationIconWithText
                      message={InfomrationMessages.HEADLINE}
                      testId={ConfirmDetailsTestIds.HEADLINE_INFO}
                    />
                  }
                  data-testid={ConfirmDetailsTestIds.TEXT_INPUT_HEADLINE}
                ></TextInput>
              </>
            )}

            <Space h="sm"></Space>
            <Button
              color={primaryColor}
              fullWidth
              radius={'lg'}
              size={'md'}
              type="submit"
              loading={isLoading}
              onClick={onNext}
            >
              NEXT
            </Button>
            {isError && (
              <Text c="red">Something went wrong, please try again</Text>
            )}
          </Stack>
        </ScrollArea>
      </Stack>

      <Space h="xl"></Space>

      {isProfessional && (
        <ProgressBar
          step={1}
          total={3}
          progressTestId={ConfirmDetailsTestIds.PROGRESS_BAR}
          textTestId={ConfirmDetailsTestIds.PROGRESS_TEXT}
        ></ProgressBar>
      )}
    </LoginWrapper>
  );
};

export enum InfomrationMessages {
  DATE_OF_BIRTH = 'Users must be at least 16 years old to create an account.',
  HEADLINE = 'A brief description that appears on your profile. E.g. Content Creator, Artist, Entrepreneur.',
}

export enum ConfirmDetailsTestIds {
  DATE_OF_BIRTH_INFO = 'date-of-birth-info',
  HEADLINE_INFO = 'headline-info',
  PROGRESS_TEXT = 'progress-text',
  PROGRESS_BAR = 'progress-bar',
  TEXT_INPUT_FIRST_NAME = 'text-input-first-name',
  TEXT_INPUT_LAST_NAME = 'text-input-last-name',
  TEXT_INPUT_DISPLAY_NAME = 'text-input-display-name',
  TEXT_INPUT_HEADLINE = 'text-input-headline',
  PERSONAL_PRIVATE_INFO_TITLE = 'personal-private-info-title',
  PUBLIC_INFO_TITLE = 'public-info-title',
  DATE_PICKER = 'date-picker',
  NEXT_BUTTON = 'next-button',
}
